import { type CSSProperties, type FC, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "./DefinitionTooltip.scss";

export interface DefinitionTooltipInfo {
  description: string;
  formula?: string;
  exampleTxt?: string;
  bullets?: string[];
  dataBasis?: string;
  note?: string;
}

interface DefinitionTooltipProps {
  label: string;
  info: DefinitionTooltipInfo;
  align?: "start" | "end";
  preferSide?: "left" | "right";
  formulaTitle?: string;
  exampleLabel?: string;
}

const DefinitionTooltip: FC<DefinitionTooltipProps> = ({
  label,
  info,
  align = "start",
  preferSide = "right",
  formulaTitle,
  exampleLabel,
}) => {
  const { t } = useTranslation();
  // Callers may pass their own dashboard-specific wording; otherwise fall back to the
  // shared translated labels rather than hardcoded English, which leaked into RTL views.
  const resolvedFormulaTitle =
    formulaTitle ?? t("definitionTooltip.formula", { defaultValue: "Formula" });
  const resolvedExampleLabel =
    exampleLabel ?? t("definitionTooltip.example", { defaultValue: "Example" });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties | undefined>();
  const [isOpen, setIsOpen] = useState(false);
  const updatePopoverPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === "undefined") {
      return;
    }
    const rect = trigger.getBoundingClientRect();

    let viewportLeftEdge = 0;
    let viewportRightEdge = window.innerWidth;
    const sidebarRect = document.querySelector(".sidebar")?.getBoundingClientRect();
    if (sidebarRect && sidebarRect.width > 0) {
      if (sidebarRect.left <= 1) {
        viewportLeftEdge = sidebarRect.right;
      } else if (sidebarRect.right >= window.innerWidth - 1) {
        viewportRightEdge = sidebarRect.left;
      }
    }

    const popupWidth = Math.min(
      viewportRightEdge - viewportLeftEdge - 16,
      window.innerWidth <= 900 ? 320 : 380,
    );
    const minLeft = viewportLeftEdge + 8;
    const maxLeft = viewportRightEdge - popupWidth - 8;
    const spaceOnRight = viewportRightEdge - rect.left - 8;
    const spaceOnLeft = rect.right - viewportLeftEdge - 8;
    const fitsOnRight = spaceOnRight >= popupWidth;
    const fitsOnLeft = spaceOnLeft >= popupWidth;
    const openRight = preferSide === "left"
      ? !fitsOnLeft && (fitsOnRight || spaceOnRight > spaceOnLeft)
      : spaceOnRight >= spaceOnLeft || (align !== "end" && fitsOnRight);
    const preferredLeft = openRight ? rect.left : rect.right - popupWidth;
    const left = Math.max(minLeft, Math.min(preferredLeft, maxLeft));

    const gap = 8;
    const margin = 8;
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openBelow = popoverHeight <= spaceBelow || spaceBelow >= spaceAbove;
    const preferredTop = openBelow ? rect.bottom + gap : rect.top - gap - popoverHeight;
    const maxTop = window.innerHeight - popoverHeight - margin;
    const top = maxTop > margin
      ? Math.max(margin, Math.min(preferredTop, maxTop))
      : Math.max(margin, preferredTop);

    setPopoverStyle({
      position: "fixed",
      left,
      right: "auto",
      top,
      width: popupWidth,
      maxWidth: "calc(100vw - 16px)",
    });
  }, [align, preferSide]);

  const handleOpen = useCallback(() => {
    updatePopoverPosition();
    setIsOpen(true);
  }, [updatePopoverPosition]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <span
      className={`definition-tooltip ${align === "end" ? "definition-tooltip--align-end" : ""
        }`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className="definition-tooltip__trigger"
        aria-label={`About ${label}`}
        onMouseEnter={handleOpen}
        onFocus={handleOpen}
        onMouseLeave={handleClose}
        onBlur={handleClose}
      >
        ?
      </button>
      {createPortal(
        <div
          ref={popoverRef}
          className={`definition-tooltip__popover${isOpen ? " definition-tooltip__popover--open" : ""}`}
          role="tooltip"
          style={popoverStyle}
        >
          <p className="definition-tooltip__text">{info?.description}</p>
          {info?.formula ? (
            <div className="definition-tooltip__formula-box">
              <div className="definition-tooltip__formula-title">{resolvedFormulaTitle}</div>
              {info?.formula?.split("\n").map((line, idx) => (
                <div key={idx} className="definition-tooltip__formula-line">
                  {line}
                </div>
              ))}
            </div>
          ) : null}
          {info?.exampleTxt ? (
            <p className="definition-tooltip__example">
              <strong className="example-txt-color">{resolvedExampleLabel}:</strong> {info?.exampleTxt}
            </p>
          ) : null}
          {info?.bullets && info?.bullets?.length > 0 ? (
            <ul className="definition-tooltip__bullets">
              {info?.bullets?.map((bullet, idx) => (
                <li key={idx} className="definition-tooltip__bullet">
                  {bullet}
                </li>
              ))}
            </ul>
          ) : null}
          {info?.dataBasis ? (
            <p className="definition-tooltip__databasis">{info?.dataBasis}</p>
          ) : null}
          {info?.note ? <p className="definition-tooltip__note">{info?.note}</p> : null}
        </div>,
        document.body,
      )}
    </span>
  );
};

export default DefinitionTooltip;
